import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ShieldCheck, Star } from "lucide-react";
import { useContent, useLang, loc, UI } from "@/lib/content";
import { resolveImg, scrollToId } from "@/lib/api";
import { LogoMark } from "./LogoMark";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { content, slot } = useContent();
  const { lang } = useLang();
  const logoSlot = slot("logo");
  const heroSlot = slot("hero");
  const ref = useRef(null);
  const cardRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 160, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-7, 7]), { stiffness: 160, damping: 18 });

  const onTilt = (e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  const showLogo = logoSlot.visible && logoSlot.url;
  const lines = content.hero_title_lines.map((l) => loc(l, lang));

  return (
    <section id="top" ref={ref} className="relative overflow-hidden bg-paper pb-16 pt-32 sm:pt-36 lg:min-h-screen lg:items-center lg:pb-24">
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[36rem] w-[36rem] rounded-full bg-brass/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-20%] left-[-8%] h-[28rem] w-[28rem] rounded-full bg-heritage/8 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-10">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease }}
            className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-heritage"
          >
            <span className="h-px w-8 bg-heritage" />
            {loc(content.hero_eyebrow, lang)}
          </motion.p>

          <h1 className="mt-6 font-display text-5xl font-semibold leading-[1.02] tracking-tight text-navy sm:text-6xl lg:text-7xl">
            {lines.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.18 + i * 0.16, ease }}
                  className="block"
                >
                  {i === lines.length - 1 ? (
                    <>
                      <span className="italic text-heritage">{line.split(",")[0]}</span>
                      {line.includes(",") ? `,${line.split(",").slice(1).join(",")}` : ""}
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease }}
            className="mt-6 max-w-xl text-base leading-relaxed text-inkbody sm:text-lg"
          >
            {loc(content.hero_subtitle, lang)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#inscripcion"
              data-testid="hero-cta-primary"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#inscripcion");
              }}
              className="rounded-full bg-heritage px-8 py-4 text-sm font-bold text-paper shadow-xl shadow-heritage/25 transition-all hover:-translate-y-0.5 hover:bg-heritagedark"
            >
              {loc(content.hero_cta_primary, lang)}
            </a>
            <a
              href="#simulador"
              data-testid="hero-cta-secondary"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#simulador");
              }}
              className="group inline-flex items-center gap-2 rounded-full border border-navy/15 px-7 py-4 text-sm font-bold text-navy transition-all hover:border-navy/40 hover:bg-white"
            >
              {loc(content.hero_cta_secondary, lang)}
              <span className="transition-transform group-hover:translate-y-0.5">↓</span>
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-6 flex items-center gap-2 text-[13px] font-semibold text-emeraldsucc"
          >
            <ShieldCheck size={16} /> {loc(content.hero_trust, lang)}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-10 grid grid-cols-3 gap-4 border-t border-line pt-6"
          >
            {UI.heroStats.map((s) => (
              <div key={s.v} data-testid="hero-stat">
                <p className="font-display text-3xl font-semibold text-navy sm:text-4xl">{s.v}</p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-mutedtext">{loc(s.label, lang)}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <motion.div style={{ y: yImg }} className="relative ml-auto w-[88%]">
            <div className="frame-arch absolute -inset-3 translate-x-2 translate-y-3 border-2 border-brass/50 sm:translate-x-4 sm:translate-y-4" />
            <div className="frame-arch relative aspect-[4/5] overflow-hidden border border-line bg-paperdeep shadow-2xl">
              {heroSlot.visible && heroSlot.url ? (
                <img
                  src={resolveImg(heroSlot.url)}
                  alt={lang === "es" ? "Preparación para la ciudadanía americana" : "American citizenship test preparation"}
                  data-testid="hero-image"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-paperdeep" data-testid="hero-image-placeholder">
                  <LogoMark className="h-16 w-16" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedtext">
                    {lang === "es" ? "Imagen del hero" : "Hero image"}
                  </p>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-navy/45 via-transparent to-transparent" />
            </div>
          </motion.div>

          <motion.div
            ref={cardRef}
            onMouseMove={onTilt}
            onMouseLeave={() => {
              mx.set(0.5);
              my.set(0.5);
            }}
            style={{ y: yCard, rotateX: rx, rotateY: ry, transformPerspective: 900 }}
            className="absolute -left-2 bottom-14 w-64 cursor-default sm:-left-8 sm:bottom-20 sm:w-72"
          >
            <div className="animate-float-slow rounded-2xl border border-linelight bg-navy p-5 text-paper shadow-2xl">
              <div className="flex items-center gap-3">
                {showLogo ? (
                  <img src={resolveImg(logoSlot.url)} alt="Logo" data-testid="hero-logo-dynamic" className="h-12 w-12 rounded-xl object-cover" />
                ) : (
                  <LogoMark className="h-12 w-12" />
                )}
                <div>
                  <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.25em] text-brass">
                    {lang === "es" ? "Pase de acceso" : "Access pass"}
                  </p>
                  <p className="font-display text-xl font-semibold leading-tight">{content.brand_name} · 2026</p>
                </div>
              </div>
              <div className="mt-4 flex items-end justify-between gap-3">
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-paper/60">USCIS · {lang === "es" ? "Actualizado" : "Updated"} 2026</p>
                <div className="flex h-6 items-stretch gap-[3px]">
                  {[3, 1, 2, 1, 3, 1, 2, 2, 1, 3].map((w, i) => (
                    <span key={i} className="w-[2px] bg-paper/50" style={{ height: "100%", transform: `scaleY(${0.4 + w * 0.2})` }} />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            style={{ y: yCard }}
            className="absolute -right-1 top-8 flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 shadow-lg sm:right-4"
          >
            <Star size={14} className="fill-brass text-brass" />
            <span className="text-[11px] font-bold text-navy">{loc(UI.includedChip, lang)}</span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
